// What an item *is*, and which fields that implies — the single source of truth
// shared by the backend (`src/modules/inventory/item-type-config.ts`) and the
// frontend (`frontend/src/lib/itemTypeConfig.ts`), both of which re-export from
// here. Mirrors how `@synapse/gestation` and `@synapse/analytics-contract` are
// wired.
//
// This package is inert: no drizzle, no Zod, no React, no I/O. It is the matrix
// and the pure predicates over it. Anything that throws the project's errors, or
// needs a translator, or knows about a table, belongs on one side or the other —
// the two re-export shims are where those live.
//
// It exists because the matrix used to be written out twice, once per side, with
// a comment on the frontend copy saying the two tsconfigs could not share code.
// That was not true — a test reached across the boundary to compare the copies
// cell by cell, which is a strange amount of machinery for a problem that goes
// away when there is one copy. The Zod schema and the form now render from the
// same object, so "the form offers a field the API rejects" is not a state that
// can be reached.
//
// Relative imports inside this package would be extensionless for the same
// reason `@synapse/analytics-contract` documents; there aren't any yet.

export const ITEM_TYPES = [
  "medication",
  "consumable",
  "medical_device",
  "reagent",
  "other",
] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

/**
 * True for item-type strings this build knows — the ones with a designed pill, a
 * field matrix and a translation.
 *
 * Server data is not restricted to them: a sixth type ships as rows long before
 * it ships as a frontend release, and every place that renders or filters one has
 * to decide what to do with a value it has never heard of. That decision needs
 * one predicate, not a copy per call site.
 */
export function isKnownItemType(value: string): value is ItemType {
  return (ITEM_TYPES as readonly string[]).includes(value);
}

export type ItemFieldKey =
  | "brandName"
  | "genericName"
  | "strength"
  | "quantity"
  | "unit"
  | "batchNumber"
  | "barcode"
  | "expiryDate"
  | "manufacturer"
  | "storageCondition"
  | "notes";

export type FieldState = "required" | "optional" | "hidden";

// Per the product spec. Don't edit one cell without checking the rest —
// the matrix is dense by design so missing entries can't drift.
export const ITEM_TYPE_CONFIG: Record<ItemType, Record<ItemFieldKey, FieldState>> = {
  medication: {
    brandName: "required",
    genericName: "required",
    strength: "required",
    quantity: "required",
    unit: "required",
    batchNumber: "optional",
    barcode: "optional",
    expiryDate: "optional",
    manufacturer: "hidden",
    storageCondition: "optional",
    notes: "optional",
  },
  consumable: {
    brandName: "required",
    genericName: "hidden",
    strength: "hidden",
    quantity: "required",
    unit: "required",
    batchNumber: "optional",
    barcode: "optional",
    expiryDate: "optional",
    manufacturer: "hidden",
    storageCondition: "hidden",
    notes: "optional",
  },
  medical_device: {
    brandName: "required",
    genericName: "hidden",
    strength: "hidden",
    quantity: "required",
    unit: "required",
    batchNumber: "optional",
    barcode: "optional",
    expiryDate: "hidden",
    manufacturer: "optional",
    storageCondition: "hidden",
    notes: "optional",
  },
  reagent: {
    brandName: "required",
    genericName: "optional",
    strength: "hidden",
    quantity: "required",
    unit: "required",
    batchNumber: "required",
    barcode: "optional",
    expiryDate: "required",
    manufacturer: "optional",
    storageCondition: "required",
    notes: "optional",
  },
  other: {
    brandName: "required",
    genericName: "hidden",
    strength: "hidden",
    quantity: "required",
    unit: "required",
    batchNumber: "optional",
    barcode: "optional",
    expiryDate: "optional",
    manufacturer: "hidden",
    storageCondition: "hidden",
    notes: "optional",
  },
};

// Suggested unit options per type. The form lets the user pick one of these
// or enter a custom string — the stored value is always free text.
export const UNIT_OPTIONS: Record<ItemType, string[]> = {
  medication: [
    "tablets",
    "capsules",
    "bottles",
    "boxes",
    "vials",
    "ml",
    "sachets",
    "ampoules",
    "drops",
  ],
  consumable: ["pieces", "boxes", "pairs", "rolls", "packs"],
  medical_device: ["units", "pieces"],
  reagent: ["tests", "strips", "ml", "vials"],
  other: ["units", "pieces"],
};

/**
 * The i18n namespace item-type labels live under.
 *
 * Here rather than spelled into each side, because both sides name these keys
 * and neither owns them: the analytics API sends `labelKey` on every `item_type`
 * member, and the frontend resolves it against its locale files. Those were two
 * hand-written spellings of one convention — the server concatenating a prefix,
 * the frontend declaring a map — with nothing but a test linking them.
 */
export const ITEM_TYPE_KEY_NAMESPACE = "itemType";

/**
 * The i18n key for an item type's display name. Resolve via
 * `t(itemTypeLabelKey(type))`; the stored `ItemType` string stays canonical.
 *
 * Takes `string`, not `ItemType`, because its callers are handling values off
 * the wire and out of the database — a type this build has never heard of still
 * has to produce a key, and the frontend's `t(key, { defaultValue })` degrades
 * that to the raw id rather than to blank text.
 */
export function itemTypeLabelKey(type: string): string {
  return `${ITEM_TYPE_KEY_NAMESPACE}.${type}`;
}

export const ITEM_TYPE_LABEL_KEY: Record<ItemType, string> = {
  medication: itemTypeLabelKey("medication"),
  consumable: itemTypeLabelKey("consumable"),
  medical_device: itemTypeLabelKey("medical_device"),
  reagent: itemTypeLabelKey("reagent"),
  other: itemTypeLabelKey("other"),
};

export function fieldState(type: ItemType, field: ItemFieldKey): FieldState {
  return ITEM_TYPE_CONFIG[type][field];
}

export function isFieldVisible(type: ItemType, field: ItemFieldKey): boolean {
  return ITEM_TYPE_CONFIG[type][field] !== "hidden";
}

export function isFieldRequired(type: ItemType, field: ItemFieldKey): boolean {
  return ITEM_TYPE_CONFIG[type][field] === "required";
}

export interface FieldViolation {
  field: ItemFieldKey;
  kind: "missing_required" | "hidden_supplied";
  message: string;
}

/**
 * Check a partial payload against the matrix for a given type. Returns one
 * violation per offending field. Treats null / undefined / empty-string as
 * "not present"; everything else is "present".
 *
 * `scope` is the set of field keys to check — callers pass only the fields
 * they own (new-item shape vs. batch fields on a stock-in entry), since the
 * matrix is consulted in both places.
 *
 * Returns violations rather than throwing, so this package stays free of the
 * project's error types. The backend turns a non-empty result into a
 * `ValidationError`; see `assertNewItemMatchesMatrix`.
 */
export function validateFieldsAgainstMatrix(
  type: ItemType,
  fields: Partial<Record<ItemFieldKey, unknown>>,
  scope: readonly ItemFieldKey[],
): FieldViolation[] {
  const cfg = ITEM_TYPE_CONFIG[type];
  const out: FieldViolation[] = [];
  for (const key of scope) {
    const value = fields[key];
    const present =
      value !== null && value !== undefined && !(typeof value === "string" && value.trim() === "");
    if (cfg[key] === "required" && !present) {
      out.push({
        field: key,
        kind: "missing_required",
        message: `${key} is required for ${type}`,
      });
    } else if (cfg[key] === "hidden" && present) {
      out.push({
        field: key,
        kind: "hidden_supplied",
        message: `${key} is not allowed for ${type}`,
      });
    }
  }
  return out;
}

/** Keys that live on the inline new-item shape. `unit`/`quantity` are always
 *  required and handled at the base Zod type, so they aren't in scope here. */
export const NEW_ITEM_FIELDS = [
  "brandName",
  "genericName",
  "strength",
  "barcode",
  "manufacturer",
  "storageCondition",
  "notes",
] as const satisfies readonly ItemFieldKey[];

/** Keys that live on the stock-in entry, alongside (but not inside) `newItem`. */
export const BATCH_FIELDS = ["batchNumber", "expiryDate"] as const satisfies readonly ItemFieldKey[];
